import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Container, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaBan, FaExchangeAlt, FaEye } from 'react-icons/fa';
import { apiClient } from '@/services/apiClient';
import './AdminReviews.scss';

interface AdminReviewSummaryDto {
  guid: string;
  reviewName: string;
  templateName: string;
  templateCategory: string;
  assignedTo: string;
  assignedBy: string;
  reviewStatus: string;
  assignedDate?: string;
  dueDate?: string;
  completedDate?: string;
  isComplete: boolean;
  totalTasks: number;
  completedTasks: number;
  progressPercent: number;
}

const AdminAllReviews: React.FC = () => {
  const [reviews, setReviews] = useState<AdminReviewSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'open' | 'all' | 'cancelled' | 'completed'>('open');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'reassign' | 'cancel'>('reassign');
  const [modalReview, setModalReview] = useState<AdminReviewSummaryDto | null>(null);
  const [modalAssigneeEmail, setModalAssigneeEmail] = useState('');
  const [modalNote, setModalNote] = useState('');
  const [modalSubmitting, setModalSubmitting] = useState(false);

  useEffect(() => {
    loadAllReviews();
  }, []);

  const loadAllReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<AdminReviewSummaryDto[]>('/api/admin/reviewmanagement/all');
      setReviews(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load all reviews');
    } finally {
      setLoading(false);
    }
  };

  const openReassignModal = (review: AdminReviewSummaryDto) => {
    setModalReview(review);
    setModalMode('reassign');
    setModalAssigneeEmail(review.assignedTo);
    setModalNote('');
    setModalOpen(true);
  };

  const openCancelModal = (review: AdminReviewSummaryDto) => {
    setModalReview(review);
    setModalMode('cancel');
    setModalAssigneeEmail(review.assignedTo);
    setModalNote('');
    setModalOpen(true);
  };

  const closeModal = () => {
    if (modalSubmitting) return;
    setModalOpen(false);
    setModalReview(null);
    setModalNote('');
  };

  const submitModalAction = async () => {
    if (!modalReview) return;

    if (modalMode === 'reassign' && !modalAssigneeEmail.trim()) {
      setActionError('New assignee email is required.');
      return;
    }

    try {
      setModalSubmitting(true);
      setActionError(null);

      if (modalMode === 'reassign') {
        await apiClient.post(`/api/admin/reviewmanagement/reviews/${modalReview.guid}/reassign`, {
          newAssigneeEmail: modalAssigneeEmail.trim(),
          note: modalNote.trim() || null,
          sessionGuid: localStorage.getItem('sessionGuid')
        });
      } else {
        await apiClient.post(`/api/admin/reviewmanagement/reviews/${modalReview.guid}/cancel`, {
          note: modalNote.trim() || null,
          sessionGuid: localStorage.getItem('sessionGuid')
        });
      }

      setModalOpen(false);
      setModalReview(null);
      setModalNote('');
      await loadAllReviews();
    } catch (err: any) {
      setActionError(err?.response?.data?.error || err?.message || `Failed to ${modalMode} review`);
    } finally {
      setModalSubmitting(false);
    }
  };

  const filteredReviews = useMemo(() => {
    const byStatus = reviews.filter((r) => {
      const normalizedStatus = (r.reviewStatus || '').toLowerCase();

      if (statusFilter === 'all') {
        return true;
      }

      if (statusFilter === 'cancelled') {
        return normalizedStatus === 'cancelled';
      }

      if (statusFilter === 'completed') {
        return r.isComplete && normalizedStatus !== 'cancelled';
      }

      // Default open/ongoing view: show non-complete, non-cancelled reviews.
      return !r.isComplete && normalizedStatus !== 'cancelled';
    });

    const q = search.trim().toLowerCase();
    if (!q) {
      return byStatus;
    }

    return byStatus.filter((r) =>
      r.reviewName.toLowerCase().includes(q) ||
      r.templateName.toLowerCase().includes(q) ||
      r.assignedTo.toLowerCase().includes(q) ||
      r.assignedBy.toLowerCase().includes(q) ||
      r.reviewStatus.toLowerCase().includes(q)
    );
  }, [reviews, search, statusFilter]);

  const stats = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const yearStart = new Date(now.getFullYear(), 0, 1);
    const isCancelled = (status?: string) => (status || '').toLowerCase() === 'cancelled';

    const open = reviews.filter(r => !r.isComplete && !isCancelled(r.reviewStatus)).length;
    const createdThisMonth = reviews.filter(r => r.assignedDate && new Date(r.assignedDate) >= monthStart).length;
    const completedThisMonth = reviews.filter(r => r.isComplete && r.completedDate && new Date(r.completedDate) >= monthStart && !isCancelled(r.reviewStatus)).length;
    const createdThisYear = reviews.filter(r => r.assignedDate && new Date(r.assignedDate) >= yearStart).length;
    const completedThisYear = reviews.filter(r => r.isComplete && r.completedDate && new Date(r.completedDate) >= yearStart && !isCancelled(r.reviewStatus)).length;
    const overdue = reviews.filter(r => !r.isComplete && r.dueDate && new Date(r.dueDate) < now && !isCancelled(r.reviewStatus)).length;

    return {
      open,
      createdThisMonth,
      completedThisMonth,
      createdThisYear,
      completedThisYear,
      overdue,
    };
  }, [reviews]);

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading all reviews...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4 px-2 px-md-3 admin-reviews-page">
      <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-2">
        <div>
          <h2 className="mb-1">Reviews</h2>
          <p className="text-muted mb-0">Admin view of all assigned and completed reviews.</p>
        </div>

        <div className="d-flex flex-wrap justify-content-start justify-content-md-end gap-2">
          <div className="border rounded px-3 py-2 text-center review-stat-tile">
            <div className="fw-bold">{stats.open}</div>
            <div className="text-muted small">Open</div>
          </div>
          <div className="border rounded px-3 py-2 text-center review-stat-tile">
            <div className="fw-bold">{stats.overdue}</div>
            <div className={`small ${stats.overdue > 0 ? 'text-danger fw-semibold' : 'text-muted'}`}>Overdue</div>
          </div>
          <div className="border rounded px-3 py-2 text-center review-stat-tile">
            <div className="fw-bold">{stats.createdThisMonth}</div>
            <div className="text-muted small">Created MTD</div>
          </div>
          <div className="border rounded px-3 py-2 text-center review-stat-tile">
            <div className="fw-bold">{stats.completedThisMonth}</div>
            <div className="text-muted small">Completed MTD</div>
          </div>
          <div className="border rounded px-3 py-2 text-center review-stat-tile">
            <div className="fw-bold">{stats.createdThisYear}</div>
            <div className="text-muted small">Created YTD</div>
          </div>
          <div className="border rounded px-3 py-2 text-center review-stat-tile">
            <div className="fw-bold">{stats.completedThisYear}</div>
            <div className="text-muted small">Completed YTD</div>
          </div>
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {actionError && <Alert variant="warning" dismissible onClose={() => setActionError(null)}>{actionError}</Alert>}

      <div className="d-flex gap-2 mb-4 flex-wrap">
        <Link to="/reviews">
          <Button variant="outline-secondary" size="sm">
            My Reviews
          </Button>
        </Link>
        <Link to="/admin/assign-review">
          <Button variant="outline-primary" size="sm">
            Assign Review
          </Button>
        </Link>
        <Link to="/admin/review-sets">
          <Button variant="outline-secondary" size="sm">
            Review Sets
          </Button>
        </Link>
      </div>

      <div className="d-flex justify-content-between align-items-center gap-3 mb-3 flex-wrap">
        <div className="d-flex gap-2 flex-wrap align-items-center">
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'open' | 'all' | 'cancelled' | 'completed')}
            style={{ maxWidth: 220 }}
            aria-label="Filter reviews by status"
          >
            <option value="open">Open/Ongoing</option>
            <option value="all">All</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
          </Form.Select>
          <Form.Control
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by review, template, assignee, assigner, or status"
            style={{ maxWidth: 540 }}
          />
        </div>
        <Badge bg="secondary" className="reviews-count-badge">{filteredReviews.length} reviews</Badge>
      </div>

      {filteredReviews.length === 0 ? (
        <Alert variant="info">No reviews match your filter.</Alert>
      ) : (
        <Table striped hover responsive>
          <thead>
            <tr>
              <th>Review</th>
              <th>Template</th>
              <th>Assigned To</th>
              <th>Assigned By</th>
              <th>Status</th>
              <th>Progress</th>
              <th>Assigned Date</th>
              <th>Due Date</th>
              <th>Completed Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredReviews.map((review) => (
              <tr key={review.guid}>
                <td>{review.reviewName}</td>
                <td>
                  <div>{review.templateName}</div>
                  <div className="small text-muted">{review.templateCategory}</div>
                </td>
                <td>{review.assignedTo}</td>
                <td>{review.assignedBy}</td>
                <td>
                  <Badge bg={review.isComplete ? 'success' : 'warning'}>
                    {review.reviewStatus}
                  </Badge>
                </td>
                <td>
                  {review.completedTasks}/{review.totalTasks} ({review.progressPercent}%)
                </td>
                <td>{review.assignedDate ? new Date(review.assignedDate).toLocaleDateString() : 'N/A'}</td>
                <td>
                  {review.dueDate
                    ? <span className={!review.isComplete && new Date(review.dueDate) < new Date() ? 'text-danger fw-semibold' : ''}>
                        {new Date(review.dueDate).toLocaleDateString()}
                      </span>
                    : <span className="text-muted">—</span>}
                </td>
                <td>{review.completedDate ? new Date(review.completedDate).toLocaleDateString() : '—'}</td>
                <td className="text-nowrap">
                  <div className="d-flex gap-2 flex-nowrap">
                    <Link to={`/admin/reviews/${review.guid}`}>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        title="View review"
                        aria-label="View review"
                      >
                        <FaEye />
                      </Button>
                    </Link>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={review.isComplete}
                      onClick={() => openReassignModal(review)}
                      title="Reassign review"
                      aria-label="Reassign review"
                    >
                      <FaExchangeAlt />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      disabled={review.isComplete}
                      onClick={() => openCancelModal(review)}
                      title="Cancel review"
                      aria-label="Cancel review"
                    >
                      <FaBan />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={modalOpen} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'reassign' ? 'Reassign Review' : 'Cancel Review'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            {modalReview
              ? modalMode === 'reassign'
                ? `Update assignee for '${modalReview.reviewName}'.`
                : `Cancel '${modalReview.reviewName}'.`
              : ''}
          </p>

          {modalMode === 'reassign' && (
            <Form.Group className="mb-3">
              <Form.Label>New Assignee Email</Form.Label>
              <Form.Control
                type="email"
                value={modalAssigneeEmail}
                onChange={(e) => setModalAssigneeEmail(e.target.value)}
                disabled={modalSubmitting}
              />
            </Form.Group>
          )}

          <Form.Group>
            <Form.Label>Optional Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={modalNote}
              onChange={(e) => setModalNote(e.target.value)}
              disabled={modalSubmitting}
              placeholder={modalMode === 'reassign' ? 'Reassignment context for audit trail' : 'Reason for cancellation'}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeModal} disabled={modalSubmitting}>
            Close
          </Button>
          <Button
            variant={modalMode === 'reassign' ? 'primary' : 'danger'}
            onClick={submitModalAction}
            disabled={modalSubmitting || (modalMode === 'reassign' && !modalAssigneeEmail.trim())}
          >
            {modalSubmitting ? 'Working...' : modalMode === 'reassign' ? 'Confirm Reassign' : 'Confirm Cancel'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminAllReviews;
